import React from 'react';
import Form from 'react-bootstrap/Form';
import {
    saveConfigureState,
    mergeConfigureState,
    getEntitySlice,
    hydrateConfigureCatalogueFromApi
} from '../utils/persistence';
import { DeleteWarningModal } from '../../../components/DeleteWarningModal';
import Load from '../../../components/loadAction';
import KpiDashboardCards from '../components/KpiDashboardCards';
import DebouncedSearchField from '../components/DebouncedSearchField';
import KpiCatalogTable from '../components/KpiCatalogTable';
import EntityModal from '../modals/EntityModal';
import QuickAddModal from '../modals/QuickAddModal';
import { ChevronRight, Plus, ChevronsDown, ChevronsUp, SquarePen, Trash2, X } from 'lucide-react';
import { isStickyHeaderStuck } from '../utils/stickyHeader';
import Modal from 'react-bootstrap/Modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOn, faToggleOff, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { PRIO_OPTS, ENTITY_COLLECTION, entityCollectionKey, findPriorityOption, normalizePriority } from '../utils/constants';
import { getHierarchyIcon } from '../utils/icons';
import { padTableRows, getHighlightedSearchText, sameId, entitySelectionKey } from '../utils/helpers';
import { buildConfigureIndexes } from '../utils/indexes';
import {
    deleteConfigureBusinessCatalogue,
    buildCatalogueDeletePayload,
    isConfigureCatalogueDeleteSuccess,
    updateConfigureBusinessCatalogueEnable,
    buildCatalogueEnablePayload,
    isConfigureCatalogueEnableSuccess,
    createConfigureBusinessCatalogue,
    buildQuickAddCataloguePayload,
    buildAddBusinessCataloguePayload,
    buildAddFunctionCataloguePayload,
    buildAddKpiCataloguePayload,
    isConfigureCatalogueCreateSuccess,
    updateConfigureBusinessCatalogue,
    buildUpdateKpiCataloguePayload,
    isConfigureCatalogueUpdateSuccess
} from '../../../utils/configureMappingEventHandle';

const MAX_CATALOG_SEARCH_ROWS = 500;

class KpiCatalogView extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            businesses: [],
            functions: [],
            kpis: [],
            models: [],
            assets: [],
            mappings: [],

            showInactive: true,
            debouncedSearchQuery: '',
            catalogFiltering: false,
            showWorkflowGuide: false,

            openNodes: {},
            isFullyExpanded: false,

            // Bulk selection — keys are `${type}:${id}`
            selectedKeys: new Set(),

            // Entity Modal
            entityModal: {
                show: false,
                type: 'business',
                id: null,
                presetParentId: null,
                name: '',
                description: '',
                priority: '',
                formula: '',
                kind: 'Forecasting',
                errors: {}
            },

            // Quick Add Modal
            quickAddModal: {
                show: false
            },
            quickAddSubmitting: false,
            entityModalSubmitting: false,

            // Delete modal
            showDeleteModal: false,
            deleteSubmitting: false,
            deleteTarget: { type: null, id: null, bulk: false, confirmName: '', bulkCount: 0 },

            // Deactivate modal
            showDeactivateModal: false,
            deactivateTarget: { type: null, id: null },

            openDropdownId: null,
            dropdownCoords: { top: 0, left: 0 },

            toast: {
                show: false,
                message: '',
                isError: false
            },
            catalogueLoading: true
        };
        this.stickySentinelRef = React.createRef();
        this.stickyHeaderRef = React.createRef();
        this._entityVersion = 0;
        this._catalogCacheKey = null;
        this._catalogTableData = null;
        this._highlightQuery = '';
        this._catalogSearchTruncated = false;
        this.visibleRowKeys = [];
    }

    componentDidMount() {
        this.loadState();
        window.addEventListener('qualdo-state-update', this.handleGlobalStateUpdate);
        document.addEventListener('click', this.handleDocumentClick);
        window.addEventListener('scroll', this.handleStickyScroll, { passive: true });

        if (typeof IntersectionObserver !== 'undefined' && this.stickySentinelRef.current) {
            this.stickyObserver = new IntersectionObserver(
                () => {
                    this.handleStickyScroll();
                },
                { threshold: [0, 1] }
            );
            this.stickyObserver.observe(this.stickySentinelRef.current);
        }
        this.handleStickyScroll();
    }

    componentDidUpdate(prevProps) {
        if (
            this.props.skipInitialFetch
            && this.props.initialCatalogueState
            && this.props.initialCatalogueState !== prevProps.initialCatalogueState
        ) {
            this.invalidateCatalogCache();
            this.setState({
                ...getEntitySlice(this.props.initialCatalogueState),
                catalogueLoading: false
            }, this.initializeOpenNodes);
        }
    }

    componentWillUnmount() {
        window.removeEventListener('qualdo-state-update', this.handleGlobalStateUpdate);
        document.removeEventListener('click', this.handleDocumentClick);
        window.removeEventListener('scroll', this.handleStickyScroll);
        if (this.stickyObserver) {
            this.stickyObserver.disconnect();
        }
    }

    handleStickyScroll = () => {
        const headerEl = this.stickyHeaderRef.current;
        const sentinelEl = this.stickySentinelRef.current;
        if (!headerEl || !sentinelEl) return;

        const isStickyStuck = isStickyHeaderStuck(sentinelEl);
        headerEl.classList.toggle('sticky-header-shadow', isStickyStuck);
    };

    invalidateCatalogCache = () => {
        this._entityVersion += 1;
        this._catalogCacheKey = null;
        this._catalogTableData = null;
        this._catalogListRows = null;
    };

    getCatalogCacheKey = () => {
        const { showInactive, debouncedSearchQuery, openNodes } = this.state;
        const q = debouncedSearchQuery.trim().toLowerCase();
        return `${this._entityVersion}|${showInactive}|${q}|${JSON.stringify(openNodes)}`;
    };

    handleDocumentClick = (event) => {
        if (!this.state.openDropdownId) return;

        // If click is inside the custom dropdown menu or the toggle button, do not close it
        const isMenuClick = event.target.closest('.custom-dropdown-menu');
        const isButtonClick = event.target.closest('.kr-actions-toggle');
        
        if (!isMenuClick && !isButtonClick) {
            this.setState({ openDropdownId: null });
        }
    };

    handleToggleDropdown = (e, id) => {
        e.stopPropagation();
        if (e.nativeEvent) {
            e.nativeEvent.stopPropagation();
            if (e.nativeEvent.stopImmediatePropagation) {
                e.nativeEvent.stopImmediatePropagation();
            }
        }
        const { openDropdownId } = this.state;
        if (openDropdownId === id) {
            this.setState({ openDropdownId: null });
        } else {
            const rect = e.currentTarget.getBoundingClientRect();
            this.setState({
                openDropdownId: id,
                dropdownCoords: {
                    top: rect.bottom + 4,
                    left: rect.right - 180
                }
            });
        }
    };

    loadState = () => {
        const preset = this.props.initialCatalogueState;
        if (this.props.skipInitialFetch && preset) {
            this.invalidateCatalogCache();
            this.setState({
                ...getEntitySlice(preset),
                catalogueLoading: false
            }, this.initializeOpenNodes);
            return;
        }

        this.setState({ catalogueLoading: true });
        hydrateConfigureCatalogueFromApi().then(({ state }) => {
            this.invalidateCatalogCache();
            this.setState({
                ...getEntitySlice(state),
                catalogueLoading: false
            }, this.initializeOpenNodes);
        });
    };

    handleGlobalStateUpdate = (e) => {
        this.invalidateCatalogCache();
        this.setState(getEntitySlice(e.detail));
    };

    initializeOpenNodes = () => {
        const nextOpen = { ...this.state.openNodes };
        this.state.businesses.forEach(b => {
            if (nextOpen['b' + b.id] === undefined) {
                nextOpen['b' + b.id] = true;
            }
        });
        this.setState({ openNodes: nextOpen });
    };

    saveState = (updatedState, callback) => {
        const next = mergeConfigureState(this.state, updatedState);
        saveConfigureState(next);
        this.invalidateCatalogCache();
        this.setState(updatedState, callback);
    };

    showToast = (message, isError = false) => {
        if (typeof this.props.showToast === 'function') {
            this.props.showToast(
                isError ? 'alert-danger' : 'alert-success',
                message,
                null,
                null,
                false
            );
            return;
        }
        this.setState({
            toast: { show: true, message, isError }
        });
    };

    hideToast = () => {
        this.setState(prevState => ({
            toast: { ...prevState.toast, show: false }
        }));
    };

    // Helper Getters — shared O(1) indexes
    getIndexes = (state = this.state) => buildConfigureIndexes(state);

    getBiz = id => this.getIndexes().getBiz(id);
    getFn = id => this.getIndexes().getFn(id);
    getKpi = id => this.getIndexes().getKpi(id);
    getModel = id => this.getIndexes().getModel(id);
    getFnsOf = bid => this.getIndexes().getFnsOf(bid);
    getKpisOf = fid => this.getIndexes().getKpisOf(fid);
    getModelsOf = fid => this.getIndexes().getModelsOf(fid);

    isBizEff = b => this.getIndexes().isBizEff(b);
    isFnEff = f => this.getIndexes().isFnEff(f);
    isKpiEff = k => this.getIndexes().isKpiEff(k);
    isModelEff = m => this.getIndexes().isModelEff(m);

    handleDebouncedSearch = (val) => {
        if (this._searchApplyTimer) {
            clearTimeout(this._searchApplyTimer);
        }
        this.setState({ catalogFiltering: true });
        this._searchApplyTimer = setTimeout(() => {
            this.setState({ debouncedSearchQuery: val, catalogFiltering: false });
            this._searchApplyTimer = null;
        }, 0);
    };

    getRegistryCounts = () => {
        const { businesses, functions, kpis, models } = this.state;
        return {
            biz: [businesses.filter(this.isBizEff).length, businesses.length],
            fn: [functions.filter(this.isFnEff).length, functions.length],
            kpi: [kpis.filter(this.isKpiEff).length, kpis.length],
            model: [models.filter(this.isModelEff).length, models.length]
        };
    };

    handleExpandAll = () => {
        const nextOpen = {};
        this.state.businesses.forEach(b => {
            nextOpen['b' + b.id] = true;
            this.getFnsOf(b.id).forEach(f => {
                nextOpen['f' + f.id] = true;
                this.getKpisOf(f.id).forEach(k => {
                    nextOpen['k' + k.id] = true;
                });
            });
        });
        this.setState({ openNodes: nextOpen, isFullyExpanded: true });
    };

    handleCollapseAll = () => {
        const nextOpen = {};
        this.state.businesses.forEach(b => {
            nextOpen['b' + b.id] = false;
        });
        this.setState({ openNodes: nextOpen, isFullyExpanded: false });
    };

    handleToggleNode = (key) => {
        this.setState(prevState => {
            const isB = key.startsWith('b');
            const current = prevState.openNodes[key] !== undefined 
                ? prevState.openNodes[key] 
                : isB;
            const willBeOpen = !current;
            return {
                openNodes: {
                    ...prevState.openNodes,
                    [key]: willBeOpen
                },
                isFullyExpanded: willBeOpen ? prevState.isFullyExpanded : false
            };
        });
    };

    // ── Bulk selection (mirrors the reference "Configure Library" bulk bar) ──
    getAllEntityKeys = () => {
        const keys = [];
        (this.state.businesses || []).forEach(b => keys.push(`business:${b.id}`));
        (this.state.functions || []).forEach(f => keys.push(`function:${f.id}`));
        (this.state.kpis || []).forEach(k => keys.push(`kpi:${k.id}`));
        (this.state.models || []).forEach(m => keys.push(`model:${m.id}`));
        return keys;
    };

    handleSelectRow = (type, id, checked) => {
        this.setState(prevState => {
            const next = new Set(prevState.selectedKeys);
            const strId = String(id);

            const affectedKeys = [entitySelectionKey(type, id)];

            if (type === 'business') {
                const childFns = (prevState.functions || []).filter(f => String(f.businessId) === strId);
                childFns.forEach(f => {
                    const fId = String(f.id);
                    affectedKeys.push(entitySelectionKey('function', fId));
                    (prevState.kpis || []).filter(k => String(k.functionId) === fId).forEach(k => affectedKeys.push(entitySelectionKey('kpi', k.id)));
                    (prevState.models || []).filter(m => String(m.functionId) === fId).forEach(m => affectedKeys.push(entitySelectionKey('model', m.id)));
                });
            } else if (type === 'function') {
                (prevState.kpis || []).filter(k => String(k.functionId) === strId).forEach(k => affectedKeys.push(entitySelectionKey('kpi', k.id)));
                (prevState.models || []).filter(m => String(m.functionId) === strId).forEach(m => affectedKeys.push(entitySelectionKey('model', m.id)));
            }

            affectedKeys.forEach(k => {
                if (checked) next.add(k);
                else next.delete(k);
            });

            (prevState.functions || []).forEach(f => {
                const fId = String(f.id);
                const kpiKeys = (prevState.kpis || []).filter(k => String(k.functionId) === fId).map(k => entitySelectionKey('kpi', k.id));
                const modelKeys = (prevState.models || []).filter(m => String(m.functionId) === fId).map(m => entitySelectionKey('model', m.id));
                const childKeys = [...kpiKeys, ...modelKeys];
                if (childKeys.length > 0) {
                    if (childKeys.every(ck => next.has(ck))) {
                        if (checked) next.add(entitySelectionKey('function', fId));
                    } else {
                        next.delete(entitySelectionKey('function', fId));
                    }
                }
            });

            (prevState.businesses || []).forEach(b => {
                const bId = String(b.id);
                const fnKeys = (prevState.functions || []).filter(f => String(f.businessId) === bId).map(f => entitySelectionKey('function', f.id));
                if (fnKeys.length > 0) {
                    if (fnKeys.every(fk => next.has(fk))) {
                        if (checked) next.add(entitySelectionKey('business', bId));
                    } else {
                        next.delete(entitySelectionKey('business', bId));
                    }
                }
            });

            return { selectedKeys: next };
        });
    };

    handleSelectAllRows = (checked) => {
        if (!checked) {
            this.setState({ selectedKeys: new Set() });
            return;
        }
        this.syncVisibleRowKeys();
        this.setState({
            selectedKeys: new Set(this.visibleRowKeys || [])
        });
    };

    syncVisibleRowKeys = () => {
        const listRows = this._catalogListRows || this.buildCatalogListRows();
        this._catalogListRows = listRows;
        this.visibleRowKeys = listRows.map((row) => entitySelectionKey(row.type, row.id));
    };

    clearSelection = () => this.setState({ selectedKeys: new Set() });

    buildDirectIdsByType = (type, id) => ({
        business: new Set(type === 'business' ? [String(id)] : []),
        function: new Set(type === 'function' ? [String(id)] : []),
        kpi: new Set(type === 'kpi' ? [String(id)] : []),
        model: new Set(type === 'model' ? [String(id)] : [])
    });

    applyCatalogueEnable = async (idsByType, enable, { onSuccess } = {}) => {
        const apiPayload = buildCatalogueEnablePayload(idsByType, enable);
        const hasApiIds = (
            apiPayload.businessIds.length
            + apiPayload.functionIds.length
            + apiPayload.kpiIds.length
        ) > 0;

        try {
            if (hasApiIds) {
                const response = await updateConfigureBusinessCatalogueEnable(apiPayload);
                if (!isConfigureCatalogueEnableSuccess(response)) {
                    this.showToast(response?.message || 'Update failed', true);
                    return false;
                }
            }

            const nextState = {};
            Object.keys(ENTITY_COLLECTION).forEach((entityType) => {
                const ids = idsByType[entityType];
                if (!ids?.size) return;
                const stateKey = ENTITY_COLLECTION[entityType];
                nextState[stateKey] = this.state[stateKey].map((item) =>
                    ids.has(String(item.id)) ? { ...item, active: enable } : item
                );
            });

            this.saveState(nextState, () => {
                if (onSuccess) onSuccess();
            });
            return true;
        } catch (e) {
            console.error('Catalogue enable update failed:', e);
            this.showToast('Update failed', true);
            return false;
        }
    };

    handleBulkActive = async (makeActive) => {
        const { selectedKeys } = this.state;
        if (!selectedKeys.size) return;

        const idsByType = this.parseSelectionIdsByType(selectedKeys);
        const count = selectedKeys.size;
        await this.applyCatalogueEnable(idsByType, makeActive, {
            onSuccess: () => {
                this.setState({ selectedKeys: new Set() });
                this.showToast(`${count} item${count === 1 ? '' : 's'} ${makeActive ? 'activated' : 'deactivated'}`);
            }
        });
    };

    parseSelectionIdsByType = (selectedKeys) => {
        const idsByType = {
            business: new Set(),
            function: new Set(),
            kpi: new Set(),
            model: new Set()
        };
        selectedKeys.forEach((key) => {
            const sep = key.indexOf(':');
            const type = key.slice(0, sep);
            const id = key.slice(sep + 1);
            if (idsByType[type]) idsByType[type].add(id);
        });
        return idsByType;
    };

    expandDeleteCascade = (idsByType) => {
        const bizIds = new Set(idsByType.business || []);
        const fnIdsSet = new Set(idsByType.function || []);
        const kpiIdsSet = new Set(idsByType.kpi || []);
        const modelIdsSet = new Set(idsByType.model || []);

        bizIds.forEach((bizId) => {
            this.getFnsOf(bizId).forEach((f) => {
                fnIdsSet.add(String(f.id));
                this.getKpisOf(f.id).forEach((k) => kpiIdsSet.add(String(k.id)));
                this.getModelsOf(f.id).forEach((m) => modelIdsSet.add(String(m.id)));
            });
        });

        fnIdsSet.forEach((fnId) => {
            this.getKpisOf(fnId).forEach((k) => kpiIdsSet.add(String(k.id)));
            this.getModelsOf(fnId).forEach((m) => modelIdsSet.add(String(m.id)));
        });

        return { bizIds, fnIdsSet, kpiIdsSet, modelIdsSet };
    };

    getDeleteEntityName = (type, id) => {
        const obj = type === 'business' ? this.getBiz(id)
            : type === 'function' ? this.getFn(id)
            : type === 'kpi' ? this.getKpi(id)
            : this.getModel(id);
        return obj?.name || '';
    };

    getDeleteEntityNameFromKey = (key) => {
        const sep = key.indexOf(':');
        if (sep <= 0) return '';
        return this.getDeleteEntityName(key.slice(0, sep), key.slice(sep + 1));
    };

    closeDeleteModal = () => {
        this.setState({
            showDeleteModal: false,
            deleteSubmitting: false,
            deleteTarget: { type: null, id: null, bulk: false, confirmName: '', bulkCount: 0 }
        });
    };

    applyLocalDeleteState = (idsByType) => {
        const { bizIds, fnIdsSet, kpiIdsSet, modelIdsSet } = this.expandDeleteCascade(idsByType);

        return {
            businesses: this.state.businesses.filter((b) => !bizIds.has(String(b.id))),
            functions: this.state.functions.filter(
                (f) => !fnIdsSet.has(String(f.id)) && !bizIds.has(String(f.businessId))
            ),
            kpis: this.state.kpis.filter(
                (k) => !kpiIdsSet.has(String(k.id)) && !fnIdsSet.has(String(k.functionId))
            ),
            models: this.state.models.filter(
                (m) => !modelIdsSet.has(String(m.id)) && !fnIdsSet.has(String(m.functionId))
            ),
            mappings: this.state.mappings.filter((m) => !kpiIdsSet.has(String(m.kpiId)))
        };
    };

    handleBulkDelete = () => {
        const { selectedKeys } = this.state;
        if (!selectedKeys.size) return;

        const count = selectedKeys.size;
        const confirmName = count === 1
            ? this.getDeleteEntityNameFromKey([...selectedKeys][0])
            : 'DELETE';

        this.setState({
            showDeleteModal: true,
            deleteTarget: {
                type: null,
                id: null,
                bulk: true,
                confirmName,
                bulkCount: count
            }
        });
    };

    handleToggleActive = (type, id) => {
        const obj = type === 'business' ? this.getBiz(id) :
                    type === 'function' ? this.getFn(id) :
                    type === 'kpi' ? this.getKpi(id) : this.getModel(id);

        if (!obj) return;

        const goingInactive = obj.active;
        let childCount = 0;
        if (type === 'business') {
            childCount = this.getFnsOf(id).length;
        } else if (type === 'function') {
            childCount = this.getKpisOf(id).length + this.getModelsOf(id).length;
        }

        const applyToggle = async () => {
            const enable = !obj.active;
            const idsByType = this.buildDirectIdsByType(type, id);
            await this.applyCatalogueEnable(idsByType, enable, {
                onSuccess: () => this.showToast(enable ? 'Activated' : 'Deactivated')
            });
        };

        if (goingInactive && childCount > 0) {
            this.setState({
                showDeactivateModal: true,
                deactivateTarget: { type, id }
            });
        } else {
            applyToggle();
        }
    };

    handleConfirmDeactivate = async () => {
        const { type, id } = this.state.deactivateTarget;
        if (!type || !id) return;

        const idsByType = this.buildDirectIdsByType(type, id);
        await this.applyCatalogueEnable(idsByType, false, {
            onSuccess: () => {
                this.setState({ showDeactivateModal: false, deactivateTarget: { type: null, id: null } });
                this.showToast('Deactivated');
            }
        });
    };

    handleDeleteEntity = (type, id) => {
        this.setState({
            showDeleteModal: true,
            deleteTarget: {
                type,
                id,
                bulk: false,
                confirmName: this.getDeleteEntityName(type, id),
                bulkCount: 0
            }
        });
    };

    handleConfirmDelete = async () => {
        const { deleteTarget } = this.state;
        const { type, id, bulk } = deleteTarget;
        if (!bulk && (!type || id == null)) return;

        let directIds;
        if (bulk) {
            directIds = this.parseSelectionIdsByType(this.state.selectedKeys);
        } else {
            directIds = this.buildDirectIdsByType(type, id);
        }

        const apiPayload = buildCatalogueDeletePayload(directIds);
        const hasApiIds = (
            apiPayload.businessIds.length
            + apiPayload.functionIds.length
            + apiPayload.kpiIds.length
        ) > 0;
        const deleteCount = bulk ? this.state.selectedKeys.size : 1;
        const modelIds = new Set(directIds.model || []);

        this.setState({ deleteSubmitting: true });

        try {
            if (hasApiIds) {
                const response = await deleteConfigureBusinessCatalogue(apiPayload);
                if (!isConfigureCatalogueDeleteSuccess(response)) {
                    this.showToast(response?.message || 'Delete failed', true);
                    this.setState({ deleteSubmitting: false });
                    return;
                }

                const { state } = await hydrateConfigureCatalogueFromApi();
                let slice = getEntitySlice(state);

                if (modelIds.size) {
                    const merged = mergeConfigureState(state, {
                        models: slice.models.filter((m) => !modelIds.has(String(m.id)))
                    });
                    saveConfigureState(merged);
                    slice = getEntitySlice(merged);
                }

                this.invalidateCatalogCache();
                this.setState({
                    ...slice,
                    showDeleteModal: false,
                    deleteSubmitting: false,
                    deleteTarget: { type: null, id: null, bulk: false, confirmName: '', bulkCount: 0 },
                    selectedKeys: new Set()
                });
                this.showToast(`${deleteCount} item${deleteCount === 1 ? '' : 's'} deleted`);
                return;
            }

            const localPatch = this.applyLocalDeleteState(directIds);
            this.saveState(localPatch, () => {
                this.setState({
                    showDeleteModal: false,
                    deleteSubmitting: false,
                    deleteTarget: { type: null, id: null, bulk: false, confirmName: '', bulkCount: 0 },
                    selectedKeys: new Set()
                });
                this.showToast(`${deleteCount} item${deleteCount === 1 ? '' : 's'} deleted`);
            });
        } catch (e) {
            console.error('Catalogue delete failed:', e);
            this.showToast('Delete failed', true);
            this.setState({ deleteSubmitting: false });
        }
    };

    handleSetPriority = (type, id, val) => {
        const key = entityCollectionKey(type);
        if (!key) return;

        let changed = false;
        const updated = this.state[key].map(item => {
            if (!sameId(item.id, id)) return item;
            changed = true;
            const nextItem = { ...item };
            const normalized = val ? normalizePriority(val, val) : '';
            if (normalized) nextItem.priority = normalized;
            else delete nextItem.priority;
            return nextItem;
        });

        if (!changed) return;

        this.saveState({ [key]: updated }, () => {
            this.showToast('Priority updated');
        });
    };

    handleCommitName = (type, id, val) => {
        const cleanVal = val.trim();
        const key = entityCollectionKey(type);

        const obj = this.state[key].find(item => item.id === id);
        if (!obj) return;

        if (!cleanVal) {
            this.showToast('Name cannot be empty', true);
            return;
        }

        let siblings = [];
        if (type === 'business') {
            siblings = this.state.businesses.filter(b => b.id !== id);
        } else if (type === 'function') {
            siblings = this.state.functions.filter(f => f.id !== id && f.businessId === obj.businessId);
        } else if (type === 'model') {
            siblings = this.state.models.filter(m => m.id !== id && m.functionId === obj.functionId);
        } else if (type === 'kpi') {
            siblings = this.state.kpis.filter(k => k.id !== id && k.functionId === obj.functionId);
        }

        const isDuplicate = siblings.some(s => s.name.toLowerCase() === cleanVal.toLowerCase());
        if (isDuplicate) {
            this.showToast('Duplicate name in the same parent group', true);
            return;
        }

        const updated = this.state[key].map(item => 
            item.id === id ? { ...item, name: cleanVal } : item
        );

        this.saveState({ [key]: updated }, () => {
            this.showToast('Renamed');
        });
    };

    openEntityModal = (type, id = null, presetParentId = null) => {
        let name = '';
        let description = '';
        let priority = '';
        let formula = '';
        let kind = 'Forecasting';
        let parentId = presetParentId;

        if (id) {
            const key = entityCollectionKey(type);
            const obj = this.state[key].find(item => item.id === id);
            if (obj) {
                name = obj.name;
                description = obj.description || obj.definition || obj.defination || '';
                priority = obj.priority || '';
                formula = obj.formula || '';
                kind = obj.kind || 'Forecasting';
                parentId = type === 'function' ? obj.businessId : 
                           (type === 'kpi' || type === 'model') ? obj.functionId : null;
            }
        } else {
            // Set default parentId if none is preset
            if (type === 'function') {
                parentId = presetParentId || this.state.businesses[0]?.id || '';
            } else if (type === 'kpi' || type === 'model') {
                parentId = presetParentId || this.state.functions[0]?.id || '';
            }
        }

        this.setState({
            entityModal: {
                show: true,
                type,
                id,
                presetParentId: parentId,
                name,
                description,
                priority,
                formula,
                kind,
                errors: {}
            }
        });
    };

    handleEntityModalSave = async () => {
        const { type, id, presetParentId, name, description, priority, formula, kind } = this.state.entityModal;
        const cleanName = name.trim();
        const errors = {};

        if (!cleanName) {
            errors.name = 'Name is required';
        }

        if (!String(description || '').trim()) {
            errors.description = 'Description is required';
        }

        if (!id && type === 'function' && !presetParentId) {
            errors.parent = 'Parent business is required';
        }

        if (!id && type === 'kpi' && !presetParentId) {
            errors.parent = 'Parent function is required';
        }

        if (Object.keys(errors).length > 0) {
            this.setState(prevState => ({
                entityModal: { ...prevState.entityModal, errors }
            }));
            return;
        }

        if (id) {
            const key = entityCollectionKey(type);
            let nextItems = [...this.state[key]];

            if (type === 'kpi') {
                this.setState({ entityModalSubmitting: true });
                try {
                    const response = await updateConfigureBusinessCatalogue(
                        buildUpdateKpiCataloguePayload({
                            id,
                            name: cleanName,
                            description,
                            priority
                        })
                    );
                    if (!isConfigureCatalogueUpdateSuccess(response)) {
                        this.showToast(response?.message || 'Save failed', true);
                        return;
                    }
                } catch (e) {
                    console.error('KPI catalogue update failed:', e);
                    this.showToast('Save failed', true);
                    return;
                } finally {
                    this.setState({ entityModalSubmitting: false });
                }
            }

            nextItems = nextItems.map(item => {
                if (item.id === id) {
                    const next = { ...item, name: cleanName, description, priority };
                    if (type === 'kpi') next.formula = formula;
                    if (type === 'model') next.kind = kind;
                    return next;
                }
                return item;
            });

            this.saveState({ [key]: nextItems }, () => {
                this.setState(prevState => ({
                    entityModal: { ...prevState.entityModal, show: false }
                }));
                this.showToast('Saved changes');
            });
            return;
        }

        if (type === 'model') {
            const key = entityCollectionKey(type);
            const newItem = {
                id: `md${Date.now()}${Math.random().toString(36).slice(2, 7)}`,
                name: cleanName,
                description,
                priority,
                kind: kind || 'Forecasting',
                functionId: presetParentId || this.state.functions[0]?.id,
                active: true
            };

            this.saveState({ [key]: [...this.state[key], newItem] }, () => {
                this.setState(prevState => ({
                    entityModal: { ...prevState.entityModal, show: false }
                }));
                this.showToast('Added item');
            });
            return;
        }

        let payload;
        if (type === 'business') {
            payload = buildAddBusinessCataloguePayload({ name: cleanName, description, priority });
        } else if (type === 'function') {
            const biz = this.getBiz(presetParentId);
            if (!biz) {
                this.showToast('Parent business not found', true);
                return;
            }
            payload = buildAddFunctionCataloguePayload(biz.name, {
                name: cleanName,
                description,
                priority
            });
        } else if (type === 'kpi') {
            const fn = this.getFn(presetParentId);
            const biz = fn ? this.getBiz(fn.businessId) : null;
            if (!fn || !biz) {
                this.showToast('Parent function not found', true);
                return;
            }
            payload = buildAddKpiCataloguePayload(biz.name, fn.name, {
                name: cleanName,
                description,
                priority
            });
        } else {
            return;
        }

        this.setState({ entityModalSubmitting: true });
        try {
            const response = await createConfigureBusinessCatalogue(payload);
            if (!isConfigureCatalogueCreateSuccess(response)) {
                this.showToast(response?.message || 'Failed to add item', true);
                this.setState({ entityModalSubmitting: false });
                return;
            }

            const { state } = await hydrateConfigureCatalogueFromApi();
            this.invalidateCatalogCache();
            this.setState({
                ...getEntitySlice(state),
                entityModal: { ...this.state.entityModal, show: false, errors: {} },
                entityModalSubmitting: false
            }, this.initializeOpenNodes);
            this.showToast('Added item');
        } catch (e) {
            console.error('Entity modal create failed:', e);
            this.showToast('Failed to add item', true);
            this.setState({ entityModalSubmitting: false });
        }
    };

    openQuickAddModal = () => {
        this.setState({
            quickAddModal: { show: true }
        });
    };

    buildQuickAddLocalModels = (values, slice) => {
        const bizName = String(values.business_name || values.business?.name || '').trim();
        const biz = slice.businesses.find((b) => b.name === bizName);
        if (!biz) return [];

        const models = [];
        (values.functions || []).forEach((fn) => {
            const fnName = String(fn.name || '').trim();
            if (!fnName) return;

            const hydratedFn = slice.functions.find(
                (f) => sameId(f.businessId, biz.id) && f.name === fnName
            );
            if (!hydratedFn) return;

            (fn.models || []).forEach((m) => {
                const name = String(m.name || '').trim();
                if (!name) return;
                models.push({
                    id: `md${Date.now()}${Math.random().toString(36).slice(2, 7)}`,
                    functionId: hydratedFn.id,
                    name,
                    description: m.description || '',
                    kind: m.kind || 'Forecasting',
                    active: true
                });
            });
        });
        return models;
    };

    handleQuickAddSubmit = async (values, { setSubmitting, resetForm }) => {
        this.setState({ quickAddSubmitting: true });
        try {
            const payload = buildQuickAddCataloguePayload(values);
            const response = await createConfigureBusinessCatalogue(payload);

            if (!isConfigureCatalogueCreateSuccess(response)) {
                this.showToast(response?.message || 'Failed to create catalogue', true);
                return;
            }

            const { state } = await hydrateConfigureCatalogueFromApi();
            let slice = getEntitySlice(state);
            const extraModels = this.buildQuickAddLocalModels(values, slice);

            if (extraModels.length) {
                const merged = mergeConfigureState(state, {
                    models: [...slice.models, ...extraModels]
                });
                saveConfigureState(merged);
                slice = getEntitySlice(merged);
            }

            this.invalidateCatalogCache();
            resetForm();
            this.setState({
                ...slice,
                quickAddModal: { show: false },
                quickAddSubmitting: false
            }, this.initializeOpenNodes);

            const fnCount = payload.function.length;
            const kpiCount = payload.function.reduce((sum, fn) => sum + (fn.kpi?.length || 0), 0);
            const reports = [`${fnCount} function${fnCount !== 1 ? 's' : ''}`];
            if (kpiCount) reports.push(`${kpiCount} KPI${kpiCount !== 1 ? 's' : ''}`);
            if (extraModels.length) reports.push(`${extraModels.length} model${extraModels.length !== 1 ? 's' : ''}`);

            this.showToast(`Added ${payload.business?.name || values.business_name} · ${reports.join(' · ')}`);
        } catch (e) {
            console.error('Quick add catalogue failed:', e);
            this.showToast('Failed to create catalogue', true);
        } finally {
            setSubmitting(false);
            this.setState({ quickAddSubmitting: false });
        }
    };

    buildCatalogListRows = () => {
        const { businesses, showInactive, debouncedSearchQuery, openNodes } = this.state;
        const q = debouncedSearchQuery.trim().toLowerCase();
        const indexes = buildConfigureIndexes(this.state);
        const listRows = [];

        const textMatches = (name, desc) => {
            if (!q) return true;
            const haystack = `${name || ''} ${desc || ''}`.toLowerCase();
            return haystack.includes(q);
        };

        businesses.forEach(b => {
            if (!showInactive && !b.active) return;

            const fns = indexes.getFnsOf(b.id).filter(f => showInactive || f.active);
            const bMatches = textMatches(b.name, b.description);

            const fnEntries = [];
            fns.forEach(f => {
                const fMatches = textMatches(f.name, f.description);
                const ks = indexes.getKpisOf(f.id).filter(k => showInactive || k.active);
                const mds = indexes.getModelsOf(f.id).filter(m => showInactive || m.active);

                const matchingKs = q ? ks.filter(k => textMatches(k.name, k.description)) : ks;
                const matchingMs = q ? mds.filter(m => textMatches(m.name, m.description)) : mds;

                if (q && !fMatches && matchingKs.length === 0 && matchingMs.length === 0) {
                    return;
                }

                fnEntries.push({ f, fMatches, matchingKs, matchingMs });
            });

            if (q && !bMatches && fnEntries.length === 0) return;

            const isOpen = q ? fnEntries.length > 0 : (openNodes['b' + b.id] !== false);
            listRows.push({
                ...b,
                type: 'business',
                depth: 0,
                openState: isOpen,
                fnCount: fns.length
            });

            if (isOpen) {
                fnEntries.forEach(({ f, matchingKs, matchingMs }, fIdx) => {
                    const isFnOpen = q
                        ? (matchingKs.length > 0 || matchingMs.length > 0)
                        : (openNodes['f' + f.id] === true);
                    const isFnLast = fIdx === fnEntries.length - 1;
                    listRows.push({
                        ...f,
                        type: 'function',
                        depth: 1,
                        openState: isFnOpen,
                        isFnLast,
                        kpiCount: matchingKs.length,
                        modelCount: matchingMs.length
                    });

                    if (isFnOpen) {
                        const leaves = [
                            ...matchingKs.map(k => ({ obj: k, type: 'kpi' })),
                            ...matchingMs.map(m => ({ obj: m, type: 'model' }))
                        ];

                        leaves.forEach((leaf, leafIdx) => {
                            const row = {
                                ...leaf.obj,
                                type: leaf.type,
                                depth: 2,
                                openState: null,
                                isFnLast,
                                isLeafLast: leafIdx === leaves.length - 1
                            };
                            if (leaf.type === 'kpi') {
                                row.mappedCount = indexes.mappingCountByKpiId.get(String(leaf.obj.id)) || 0;
                            }
                            listRows.push(row);
                        });
                    }
                });
            }
        });

        this._catalogSearchTruncated = false;
        if (q && listRows.length > MAX_CATALOG_SEARCH_ROWS) {
            listRows.length = MAX_CATALOG_SEARCH_ROWS;
            this._catalogSearchTruncated = true;
        }
        return listRows;
    };

    buildCatalogTableData = () => {
        const cacheKey = this.getCatalogCacheKey();
        const q = this.state.debouncedSearchQuery.trim().toLowerCase();
        this._highlightQuery = q;

        let listRows;
        if (this._catalogCacheKey === cacheKey && this._catalogListRows) {
            listRows = this._catalogListRows;
        } else {
            listRows = this.buildCatalogListRows();
            this._catalogListRows = listRows;
        }
        this.visibleRowKeys = listRows.map((row) => entitySelectionKey(row.type, row.id));

        if (this._catalogCacheKey === cacheKey && this._catalogTableData) {
            return this._catalogTableData;
        }

        const headers = ['Business Hierarchy', 'Select', 'Active', 'Description', 'Status', 'Priority', 'Actions'];
        const formattedRows = listRows.map((rowItem) => [
            { type: 'component', value: '', row: rowItem },
            { type: 'component', value: '', row: rowItem },
            { type: 'component', value: '', row: rowItem },
            { type: 'td', value: rowItem.description || rowItem.definition || rowItem.defination || '', row: rowItem },
            { type: 'component', value: '', row: rowItem },
            { type: 'component', value: '', row: rowItem },
            { type: 'component', value: '', row: rowItem }
        ]);

        const { rows: paddedRows } = padTableRows(formattedRows, { targetLimit: 15, columnCount: 7 });

        this._catalogCacheKey = cacheKey;
        this._catalogTableData = {
            headers,
            data: paddedRows,
            not_sortable: true
        };
        return this._catalogTableData;
    };

    renderCatalogTable = () => {
        const tableData = this.buildCatalogTableData();

        return (
            <>
                {this._catalogSearchTruncated && (
                    <div className="tw-px-4 tw-py-2 tw-text-xs tw-font-medium tw-text-amber-800 dark:tw-text-amber-200 tw-bg-amber-50 dark:tw-bg-amber-500/10 tw-border-0 tw-border-b tw-border-solid tw-border-amber-200 dark:tw-border-amber-500/20">
                        Showing the first {MAX_CATALOG_SEARCH_ROWS} matches. Refine your search to narrow results.
                    </div>
                )}
                <KpiCatalogTable
                    tableData={tableData}
                    selectedKeys={this.state.selectedKeys}
                    highlightQuery={this._highlightQuery}
                    entityVersion={this._entityVersion}
                    customGetActionComponent={this.customGetActionComponent}
                    customHeaderFormatter={this.customHeaderFormatter}
                    customRowHighlighter={this.customRowHighlighter}
                />
            </>
        );
    };

    customGetActionComponent = (actionType, headerValue, cell, tableProps, rowIndex) => {
        const renderHighlightedLabel = (text) =>
            getHighlightedSearchText(text, this._highlightQuery);
        const rowDataList = tableProps.data?.data;
        if (!rowDataList || !rowDataList[rowIndex]) return null;

        const row = cell?.row || rowDataList[rowIndex][0]?.row;
        const cleanHeader = headerValue.replace(' ⇅', '').trim();

        if (!row || row.isDummy) {
            // Tag padding rows so CSS can hide them (see .kr-dummy-marker rule).
            if (cleanHeader === "Actions" && row && row.isDummy) {
                return <span className="kr-dummy-marker" />;
            }
            return null;
        }

        if (cleanHeader === "Select") {
            const key = entitySelectionKey(row.type, row.id);
            const chkId = `select-kpi-row-${key}`;
            const isChecked = this.state.selectedKeys.has(key);
            return (
                <div className="tw-flex tw-justify-center">
                    <div className="custom-control custom-checkbox">
                        <input
                            type="checkbox"
                            className="custom-control-input"
                            id={chkId}
                            checked={isChecked}
                            onChange={e => this.handleSelectRow(row.type, row.id, e.target.checked)}
                        />
                        <label className="custom-control-label" htmlFor={chkId}></label>
                    </div>
                </div>
            );
        }

        if (cleanHeader === "Active") {
            const type = row.type;
            const isOwnActive = row.active;
            const isAncestorInactive = type === 'function' ? !this.isBizEff(this.getBiz(row.businessId)) :
                                       (type === 'kpi' || type === 'model') ? !this.isFnEff(this.getFn(row.functionId)) : false;
            return (
                <div className="tw-text-center tw-flex tw-justify-start">
                    <Form.Check 
                        type="switch"
                        id={`active-switch-${row.id}`}
                        checked={isOwnActive}
                        disabled={isAncestorInactive}
                        onChange={() => !isAncestorInactive && this.handleToggleActive(type, row.id)}
                        className="tw-cursor-pointer"
                    />
                </div>
            );
        }

        if (cleanHeader === "Business Hierarchy") {
            const { type, openState, name, id, isFnLast, isLeafLast } = row;

            const levelIcon = getHierarchyIcon(type);

            let childMeta = '';
            if (type === 'business') {
                childMeta = `${row.fnCount ?? this.getFnsOf(id).length} Fn`;
            } else if (type === 'function') {
                const kpiCount = row.kpiCount ?? this.getKpisOf(id).length;
                const modelCount = row.modelCount ?? this.getModelsOf(id).length;
                childMeta = modelCount > 0
                    ? `${kpiCount} KPI · ${modelCount} model`
                    : `${kpiCount} KPI`;
            }

            const countBadge = childMeta ? (
                <span className="tw-text-[10.5px] tw-font-bold tw-text-[#475569] dark:tw-text-gray-300 tw-ml-auto tw-whitespace-nowrap tw-shrink-0 tw-bg-[#EEF1F6] dark:tw-bg-white/10 tw-border tw-border-solid tw-border-[#E3E8F1] dark:tw-border-white/10 tw-rounded-full tw-px-2 tw-py-0.5">
                    {childMeta}
                </span>
            ) : null;

            if (type === 'business') {
                return (
                    <div
                        className="tw-flex tw-items-center tw-justify-between tw-w-full tw-min-h-[48px] tw-h-full tw-px-3 tw-text-left tw-cursor-pointer"
                        onClick={() => this.handleToggleNode('b' + id)}
                    >
                        <div className="tw-flex tw-items-center tw-flex-1 tw-min-w-0 tw-mr-2 tw-text-left">
                            <span className={`tw-inline-flex tw-w-5 tw-items-center tw-justify-center tw-text-[#94A3B8] tw-transition-transform tw-duration-150 tw-shrink-0 tw-mr-1 ${openState ? 'tw-rotate-90' : ''}`}>
                                <ChevronRight size={14} />
                            </span>
                            <span className="tw-inline-flex tw-items-center tw-text-[#334155] dark:tw-text-gray-300 tw-shrink-0 tw-mr-2">{levelIcon}</span>
                            <span className="tw-text-[14px] tw-font-bold tw-text-[#0F172A] dark:tw-text-white tw-truncate tw-block tw-min-w-0 tw-flex-1 tw-text-left" title={name}>
                                {renderHighlightedLabel(name)}
                            </span>
                        </div>
                        {countBadge}
                    </div>
                );
            }

            if (type === 'function') {
                return (
                    <div
                        className="tw-flex tw-items-center tw-w-full tw-min-h-[48px] tw-h-full tw-px-3 tw-text-left tw-cursor-pointer"
                        onClick={() => this.handleToggleNode('f' + id)}
                    >
                        <span className={`tw-relative tw-w-[22px] tw-self-stretch tw-shrink-0 before:tw-content-[''] before:tw-absolute before:tw-left-[11px] before:tw-top-[-12px] before:tw-w-0 before:tw-border-0 before:tw-border-l-[1.5px] before:tw-border-solid before:tw-border-[#E2E8F0] dark:before:tw-border-white/10 ${isFnLast ? 'before:tw-h-[calc(50%+12px)]' : 'before:tw-h-[calc(100%+24px)]'} after:tw-content-[''] after:tw-absolute after:tw-left-[11px] after:tw-top-1/2 after:tw-w-3 after:tw-h-0 after:tw-border-0 after:tw-border-t-[1.5px] after:tw-border-solid after:tw-border-[#E2E8F0] dark:after:tw-border-white/10`} />
                        <div className="tw-flex tw-items-center tw-flex-1 tw-min-w-0 tw-mr-2 tw-text-left">
                            <span className={`tw-inline-flex tw-w-5 tw-items-center tw-justify-center tw-text-[#94A3B8] tw-transition-transform tw-duration-150 tw-shrink-0 tw-mr-1 ${openState ? 'tw-rotate-90' : ''}`}>
                                <ChevronRight size={14} />
                            </span>
                            <span className="tw-inline-flex tw-items-center tw-text-[#64748B] dark:tw-text-gray-400 tw-shrink-0 tw-mr-2">{levelIcon}</span>
                            <span className="tw-text-[13px] tw-font-semibold tw-text-[#475569] dark:tw-text-gray-300 tw-truncate tw-block tw-min-w-0 tw-flex-1 tw-text-left" title={name}>
                                {renderHighlightedLabel(name)}
                            </span>
                        </div>
                        {countBadge}
                    </div>
                );
            }

            // KPI / Model leaf row
            return (
                <div className="tw-flex tw-items-center tw-w-full tw-min-h-[48px] tw-h-full tw-px-3 tw-text-left">
                    <span className={`tw-relative tw-w-[22px] tw-self-stretch tw-shrink-0 ${isFnLast ? '' : "before:tw-content-[''] before:tw-absolute before:tw-left-[11px] before:tw-top-[-12px] before:tw-w-0 before:tw-h-[calc(100%+24px)] before:tw-border-0 before:tw-border-l-[1.5px] before:tw-border-solid before:tw-border-[#E2E8F0] dark:before:tw-border-white/10"}`} />
                    <span className={`tw-relative tw-w-[22px] tw-self-stretch tw-shrink-0 before:tw-content-[''] before:tw-absolute before:tw-left-[11px] before:tw-top-[-12px] before:tw-w-0 before:tw-border-0 before:tw-border-l-[1.5px] before:tw-border-solid before:tw-border-[#E2E8F0] dark:before:tw-border-white/10 ${isLeafLast ? 'before:tw-h-[calc(50%+12px)]' : 'before:tw-h-[calc(100%+24px)]'} after:tw-content-[''] after:tw-absolute after:tw-left-[11px] after:tw-top-1/2 after:tw-w-3 after:tw-h-0 after:tw-border-0 after:tw-border-t-[1.5px] after:tw-border-solid after:tw-border-[#E2E8F0] dark:after:tw-border-white/10`} />
                    <span className="tw-inline-flex tw-items-center tw-text-[#64748B] dark:tw-text-gray-400 tw-shrink-0 tw-ml-3 tw-mr-2">{levelIcon}</span>
                    <span className="tw-text-[13px] tw-font-semibold tw-text-[#0F172A] dark:tw-text-white tw-truncate tw-block tw-min-w-0 tw-flex-1 tw-text-left" title={name}>
                        {renderHighlightedLabel(name)}
                    </span>
                </div>
            );
        }

        if (cleanHeader === "Description") {
            return (
                <span 
                    className="tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-text-sm tw-font-normal tw-block tw-break-words tw-whitespace-normal tw-w-full" 
                    style={{ width: '100%' }}
                    title={cell}
                >
                    {cell
                        ? renderHighlightedLabel(cell)
                        : <span className="tw-text-gray-300 dark:tw-text-gray-600">&mdash;</span>}
                </span>
            );
        }

        if (cleanHeader === "Type") {
            const { type, kind } = row;
            return type === 'model' ? (
                <span className="tw-inline-flex tw-items-center tw-gap-5 tw-text-purple-700 tw-border tw-border-solid tw-border-purple-200 tw-bg-purple-50 tw-text-[11px] tw-font-semibold tw-px-2.5 tw-py-0.5 tw-rounded-full">
                    {kind || 'Model'}
                </span>
            ) : (
                <span className="tw-text-[12px] tw-text-gray-300">&mdash;</span>
            );
        }

        if (cleanHeader === "Mapped") {
            const { type, mappedCount } = row;
            return type === 'kpi' ? (
                <span className="tw-text-[#535862] dark:tw-text-[#B2B5BE] tw-text-[13px] tw-font-normal">
                    {mappedCount ?? 0} mapped
                </span>
            ) : (
                <span className="tw-text-[12px] tw-text-gray-300 dark:tw-text-gray-600">&mdash;</span>
            );
        }

        if (cleanHeader === "Priority") {
            const { type, id } = row;
            const collectionKey = entityCollectionKey(type);
            const liveItem = collectionKey
                ? (this.state[collectionKey] || []).find((item) => sameId(item.id, id))
                : null;
            const prioVal = findPriorityOption(liveItem?.priority)?.value || '';

            return (
                <select
                    value={prioVal}
                    onChange={(e) => {
                        e.stopPropagation();
                        this.handleSetPriority(type, id, e.target.value);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className="tw-w-[100px] tw-h-6 tw-text-[11px] tw-font-semibold tw-rounded-md tw-border tw-border-solid tw-border-[#e2e8f0] dark:tw-border-white/10 tw-bg-white dark:tw-bg-[#2B2B40] tw-text-[#181D27] dark:tw-text-[#F1F5F9] tw-px-2 tw-cursor-pointer"
                    aria-label="Set priority"
                >
                    <option value="">Set priority</option>
                    {PRIO_OPTS.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                </select>
            );
        }

        if (cleanHeader === "Actions" || cleanHeader === "") {
            const { type, id } = row;
            return (
                <div className="tw-flex tw-items-center tw-justify-end tw-gap-1.5 tw-w-full">
                    {type === 'business' && (
                        <button
                            type="button"
                            onClick={() => this.openEntityModal('function', null, id)}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-border tw-border-solid tw-rounded-lg tw-transition-colors tw-text-[#1D4ED8] dark:tw-text-blue-400 tw-bg-[#EFF6FF] dark:tw-bg-blue-500/10 tw-border-[#BFDBFE] dark:tw-border-blue-500/20 hover:tw-bg-[#DBEAFE] dark:hover:tw-bg-blue-500/20 tw-cursor-pointer"
                        >
                            <Plus size={12} className="tw-stroke-[2.5]" />
                            <span>Function</span>
                        </button>
                    )}
                    {type === 'function' && (
                        <button
                            type="button"
                            onClick={() => this.openEntityModal('kpi', null, id)}
                            className="tw-inline-flex tw-items-center tw-gap-1 tw-px-2 tw-py-1 tw-text-xs tw-font-semibold tw-border tw-border-solid tw-rounded-lg tw-transition-colors tw-text-[#1D4ED8] dark:tw-text-blue-400 tw-bg-[#EFF6FF] dark:tw-bg-blue-500/10 tw-border-[#BFDBFE] dark:tw-border-blue-500/20 hover:tw-bg-[#DBEAFE] dark:hover:tw-bg-blue-500/20 tw-cursor-pointer"
                        >
                            <Plus size={12} className="tw-stroke-[2.5]" />
                            <span>KPI</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={() => this.openEntityModal(type, id)}
                        className="tw-w-[30px] tw-h-[30px] tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-text-[#475569] dark:tw-text-[#94A3B8] tw-bg-[#F1F5F9] dark:tw-bg-white/5 hover:tw-bg-[#EFF6FF] dark:hover:tw-bg-blue-500/20 hover:tw-text-[#1D4ED8] dark:hover:tw-text-blue-400 tw-transition-colors tw-border-none tw-cursor-pointer"
                        title="Edit"
                        aria-label="Edit"
                    >
                        <SquarePen size={15} />
                    </button>
                    <button
                        type="button"
                        onClick={() => this.handleDeleteEntity(type, id)}
                        className="tw-w-[30px] tw-h-[30px] tw-inline-flex tw-items-center tw-justify-center tw-rounded-lg tw-text-[#475569] dark:tw-text-[#94A3B8] tw-bg-[#F1F5F9] dark:tw-bg-white/5 hover:tw-bg-[#FEE2E2] dark:hover:tw-bg-red-500/20 hover:tw-text-[#DC2626] dark:hover:tw-text-red-400 tw-transition-colors tw-border-none tw-cursor-pointer"
                        title="Delete"
                        aria-label="Delete"
                    >
                        <Trash2 size={15} />
                    </button>
                </div>
            );
        }

        return null;
    };

    // Renders the select-all checkbox inside the "Select" header cell.
    customHeaderFormatter = (headerValue) => {
        const cleanHeader = String(headerValue || '').replace(' ⇅', '').trim();
        if (cleanHeader !== 'Select') return headerValue;

        this.syncVisibleRowKeys();
        const visible = this.visibleRowKeys || [];
        const selected = this.state.selectedKeys;
        const allSelected = visible.length > 0 && visible.every((key) => selected.has(key));
        const someSelected = visible.some((key) => selected.has(key));

        return (
            <div className="tw-flex tw-justify-center" onClick={(e) => e.stopPropagation()}>
                <div className="custom-control custom-checkbox">
                    <input
                        type="checkbox"
                        className="custom-control-input"
                        id="select-all-kpi-rows"
                        checked={allSelected}
                        ref={(el) => {
                            if (el) el.indeterminate = !allSelected && someSelected;
                        }}
                        onChange={(e) => {
                            e.stopPropagation();
                            this.handleSelectAllRows(e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                    />
                    <label
                        className="custom-control-label"
                        htmlFor="select-all-kpi-rows"
                        onClick={(e) => e.stopPropagation()}
                    />
                </div>
            </div>
        );
    };

    customRowHighlighter = (row) => {
        const rowItem = row.Actions?.row || row["Business Hierarchy"]?.row;
        if (rowItem) {
            if (rowItem.type === 'business') return 'tbl-row dm-treerow dm-biz';
            if (rowItem.type === 'function') return 'tbl-row dm-treerow dm-fn';
            if (rowItem.type === 'kpi' || rowItem.type === 'model') return 'tbl-row dm-treerow dm-leaf';
        }
        return 'tbl-row dm-treerow';
    };

    render() {
        if (this.state.catalogueLoading) {
            return (
                <div className="portlet pb-0">
                    <div className="portlet-body tw-py-10">
                        <Load />
                    </div>
                </div>
            );
        }

        const { 
            businesses,
            functions,
            showInactive, 
            debouncedSearchQuery,
            catalogFiltering,
            entityModal, 
            quickAddModal 
        } = this.state;

        const counts = this.getRegistryCounts();

        return (
            <>
                {/* 1. Business Library Portlet */}
                <div className="portlet">
                    <div className="portlet-title tw-flex tw-items-center tw-justify-between after:!tw-content-none after:!tw-hidden before:!tw-content-none before:!tw-hidden !tw-border-none">
                        <div className="caption">
                            <span className="text-uppercase font-dark">Business library</span>
                            {/* <small className='tw-text-sm tw-text-[#535862] tw-block'>Define the governed hierarchy: Business &rarr; Function &rarr; KPI / Model. Map KPI data fields separately in Domain Mapping.</small> */}
                            <small className='tw-text-sm tw-text-[#535862] tw-block'>Define the governed hierarchy: Business &rarr; Function &rarr; KPI. Map KPI data fields separately in Domain Mapping.</small>

                        </div>
                        {/* <div className="actions !tw-flex tw-items-center tw-gap-4">
                            <button 
                                onClick={() => this.setState(prev => ({ showWorkflowGuide: !prev.showWorkflowGuide }))}
                                className="tw-h-7 tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg tw-bg-white dark:tw-bg-[#2B2B40] tw-text-[#374151] dark:tw-text-white tw-text-xs tw-font-semibold tw-px-2.5 tw-transition-colors hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#3A3A55] tw-cursor-pointer"
                            >
                                <Info className="tw-w-3.5 tw-h-3.5" />
                                <span>{showWorkflowGuide ? 'Hide' : 'Show'} workflow guide</span>
                            </button>
                        </div> */}
                    </div>

                    <div className='portlet-body !tw-pt-0 tw-relative !tw-overflow-visible'>
                        {/* Modular KPI Summary Cards */}
                        <KpiDashboardCards counts={counts} />
                    </div>
                </div>

                {/* 2. Configure Library Portlet */}
                <div className="portlet pb-0">
                    <div className="portlet-title tw-flex tw-items-center tw-justify-between after:!tw-content-none after:!tw-hidden before:!tw-content-none before:!tw-hidden">
                        <div className="caption">
                            <span className="text-uppercase font-dark">Configure Library</span>
                            <small className='tw-text-sm tw-text-[#535862] tw-block'>Rename inline, add children from each row, and use toggles to activate or deactivate without deleting records.</small>
                        </div>
                    </div>

                    <div className='portlet-body !tw-p-0 tw-relative !tw-overflow-visible'>
                        {/* Controls Sub-header */}
                        <div ref={this.stickySentinelRef} className="tw-h-0 tw-w-full tw-pointer-events-none" />
                        <div ref={this.stickyHeaderRef} className="tw-p-4 tw-flex tw-flex-col md:tw-flex-row md:tw-items-center tw-justify-between tw-gap-3 md:tw-gap-4 tw-bg-white dark:tw-bg-[#1E1E2D] tw-border-0 tw-border-b tw-border-solid tw-border-[#ECECEC] dark:tw-border-white/10 tw-sticky tw-top-[64px] tw-z-20">
                            {/* Left: Show inactive toggle */}
                            <div className="tw-flex tw-items-center tw-shrink-0">
                                <button
                                    type="button"
                                    onClick={() => this.setState({ showInactive: !showInactive })}
                                    className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-border tw-border-solid tw-rounded-lg tw-transition-colors tw-text-[#374151] dark:tw-text-white tw-bg-white dark:tw-bg-[#2B2B40] tw-border-[#D5D7DA] dark:tw-border-white/10 hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#3A3A55] tw-cursor-pointer tw-h-[38px]"
                                >
                                    <input 
                                        type="checkbox" 
                                        checked={showInactive}
                                        readOnly
                                        className="tw-w-[15px] tw-h-[15px] tw-rounded tw-accent-blue-600 tw-cursor-pointer"
                                    />
                                    <span className="tw-whitespace-nowrap">Show inactive</span>
                                </button>
                            </div>

                            {/* Center: Searchbar */}
                            <DebouncedSearchField
                                value={debouncedSearchQuery}
                                onDebouncedChange={this.handleDebouncedSearch}
                                debounceMs={400}
                                placeholder="Search business, function, KPI..."
                                wrapperClassName={`md:tw-flex-1 md:tw-max-w-[700px] ${catalogFiltering ? 'tw-opacity-70' : ''}`}
                                inputClassName="tw-w-full tw-pl-[38px] tw-pr-10 tw-py-[6px] tw-text-sm tw-text-[#171717] dark:tw-text-white tw-bg-white dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg focus:tw-outline-none focus:tw-border-[#4F46E5] dark:focus:tw-border-[#818CF8]"
                            />

                            {/* Right: Action Buttons (Expand all + Add New) */}
                            <div className="tw-flex tw-items-center tw-justify-between md:tw-justify-end tw-gap-3 tw-w-full md:tw-w-auto tw-shrink-0">
                                {!this.state.isFullyExpanded ?
                                    <button
                                        onClick={this.handleExpandAll}
                                        className="tw-h-9 tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg tw-bg-white dark:tw-bg-[#2B2B40] tw-text-[#374151] dark:tw-text-white tw-text-[11.5px] tw-font-semibold tw-px-2.5 tw-transition-colors hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#3A3A55] tw-cursor-pointer tw-whitespace-nowrap"
                                    >
                                        <ChevronsDown className="tw-w-3.5 tw-h-3.5" />
                                        <span>Expand all</span>
                                    </button>
                                    :
                                    <button
                                        onClick={this.handleCollapseAll}
                                        className="tw-h-9 tw-inline-flex tw-items-center tw-justify-center tw-gap-1 tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg tw-bg-white dark:tw-bg-[#2B2B40] tw-text-[#374151] dark:tw-text-white tw-text-[11.5px] tw-font-semibold tw-px-2.5 tw-transition-colors hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#3A3A55] tw-cursor-pointer tw-whitespace-nowrap"
                                    >
                                        <ChevronsUp className="tw-w-3.5 tw-h-3.5" />
                                        <span>Collapse all</span>
                                    </button>
                                }
                                <button 
                                    onClick={this.openQuickAddModal} 
                                    className="tw-inline-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white tw-bg-[#EF4444] hover:tw-bg-[#DC2626] tw-rounded-lg tw-transition-colors tw-border-none tw-cursor-pointer tw-whitespace-nowrap"
                                >
                                    <Plus size={16} />
                                    <span>Add New</span>
                                </button>
                            </div>
                        </div>

                        {/* Bulk-select action bar */}
                        {this.state.selectedKeys.size > 0 && (
                            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-5 tw-py-[9px] tw-bg-[#EFF6FF] dark:tw-bg-[#13131F] tw-border-0 tw-border-b tw-border-solid tw-border-[#DBEAFE] dark:tw-border-[#334155]">
                                <span className="tw-text-[12.5px] tw-font-semibold tw-text-[#1E40AF] dark:tw-text-[#93C5FD]">
                                    <span className="tw-inline-flex tw-items-center tw-justify-center tw-min-w-[20px] tw-h-5 tw-px-1.5 tw-rounded-full tw-bg-[#2563EB] tw-text-white tw-text-[11px] tw-font-bold tw-mr-1.5">
                                        {this.state.selectedKeys.size}
                                    </span>
                                    selected
                                </span>
                                <div className="tw-flex tw-items-center tw-gap-2">
                                    <button
                                        type="button"
                                        onClick={() => this.handleBulkActive(true)}
                                        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-[#374151] dark:tw-text-white tw-bg-white dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#3A3A55] tw-cursor-pointer tw-transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faToggleOn} className="tw-text-[#007bff]" style={{ fontSize: '15px' }} />
                                        Activate
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => this.handleBulkActive(false)}
                                        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-[#374151] dark:tw-text-white tw-bg-white dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg hover:tw-bg-[#F9FAFB] dark:hover:tw-bg-[#3A3A55] tw-cursor-pointer tw-transition-colors"
                                    >
                                        <FontAwesomeIcon icon={faToggleOff} className="tw-text-[#94A3B8]" style={{ fontSize: '15px' }} />
                                        Deactivate
                                    </button>
                                    <span className="tw-w-px tw-h-[18px] tw-bg-[#BFD3F2] dark:tw-bg-[#334155]" aria-hidden="true" />
                                    <button
                                        type="button"
                                        onClick={this.handleBulkDelete}
                                        className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-semibold tw-text-[#DC2626] dark:tw-text-[#F87171] tw-bg-white dark:tw-bg-[#2B2B40] tw-border tw-border-solid tw-border-[#D5D7DA] dark:tw-border-white/10 tw-rounded-lg hover:tw-bg-[#FEF2F2] dark:hover:tw-bg-[#3A2020] tw-cursor-pointer tw-transition-colors"
                                    >
                                        <Trash2 size={13} />
                                        Delete
                                    </button>
                                    <button
                                        type="button"
                                        onClick={this.clearSelection}
                                        className="tw-inline-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-p-0 tw-text-[#6B7280] dark:tw-text-[#94A3B8] tw-bg-transparent tw-border-none tw-rounded-full hover:tw-bg-blue-100 dark:hover:tw-bg-white/10 tw-cursor-pointer tw-transition-colors"
                                        aria-label="Clear selection"
                                    >
                                        <X size={15} />
                                    </button>
                                </div>
                            </div>
                        )}

                        {/* Table Body */}
                        <div id="kpi-body" className="tw-p-0">
                            {this.renderCatalogTable()}
                        </div>
                    </div>
                </div>

                {/* MODAL DIALOGS */}
                {/* Delete Warning Modal */}
                <DeleteWarningModal 
                    delete_show={this.state.showDeleteModal}
                    deleteClose={this.closeDeleteModal}
                    handleDeleteFunction={this.handleConfirmDelete}
                    component="configureCatalogue"
                    entityLabel={
                        this.state.deleteTarget.bulk && this.state.deleteTarget.bulkCount > 1
                            ? 'Selected Items'
                            : (this.state.deleteTarget.type === 'business'
                                ? 'Business area'
                                : this.state.deleteTarget.type
                                    ? this.state.deleteTarget.type.charAt(0).toUpperCase() + this.state.deleteTarget.type.slice(1)
                                    : 'Item')
                    }
                    confirmName={this.state.deleteTarget.confirmName}
                    bulkCount={this.state.deleteTarget.bulk ? this.state.deleteTarget.bulkCount : undefined}
                    deleting={this.state.deleteSubmitting}
                />

                {/* Deactivate Warning Modal */}
                {this.state.showDeactivateModal && (() => {
                    const { type, id } = this.state.deactivateTarget;
                    const obj = type === 'business' ? this.getBiz(id) :
                                type === 'function' ? this.getFn(id) : null;
                    if (!obj) return null;

                    const label = type === 'business' ? 'Business' : 'Function';
                    let childCount = 0;
                    if (type === 'business') {
                        childCount = this.getFnsOf(id).length;
                    } else if (type === 'function') {
                        childCount = this.getKpisOf(id).length + this.getModelsOf(id).length;
                    }

                    return (
                        <Modal show={this.state.showDeactivateModal} centered onHide={() => this.setState({ showDeactivateModal: false, deactivateTarget: { type: null, id: null } })}>
                            <Modal.Header closeButton className="d-none">
                                <h5 className="modal-title">{`Deactivate ${label}`}</h5>
                            </Modal.Header>
                            <Modal.Body>
                                <div className="modal__alert">
                                    <div className="modal_alert-icon">
                                        <i className="text-danger">
                                            <FontAwesomeIcon icon={faToggleOn} />
                                        </i>
                                    </div>
                                    <div className="confirmation-alert_text">
                                        <h4>{`Deactivate ${label}?`}</h4>
                                        <p>
                                            Please confirm the name of the {type} to deactivate: <strong>{obj.name}</strong>
                                        </p>
                                    </div>

                                    <div className="confirmation-alert_form">
                                        <div className="alert alert-danger text-left">
                                            <i className="text-danger mr-2">
                                                <FontAwesomeIcon icon={faExclamationTriangle} />
                                            </i>
                                            <strong>{obj.name}</strong> has {childCount} child item(s). They'll be greyed out as <em>inactive</em> and their data-asset mappings will be locked in Domain Mapping — nothing is deleted. Reactivate any time to restore.
                                        </div>
                                    </div>

                                    <div className="modal_alert-btn-group">
                                        <button 
                                            type="button" 
                                            onClick={() => this.setState({ showDeactivateModal: false, deactivateTarget: { type: null, id: null } })}
                                            className="btn btn-outline btn-grey btn-circle mr-2"
                                        >
                                            Cancel
                                        </button>
                                        <button 
                                            type="button"
                                            onClick={this.handleConfirmDeactivate}
                                            className="btn btn-primary btn-circle mr-2"
                                        >
                                            Deactivate
                                        </button>
                                    </div>
                                </div>
                            </Modal.Body>
                        </Modal>
                    );
                })()}

                {/* 2. Add / Edit Entity Modal */}
                <EntityModal 
                    show={entityModal.show}
                    onClose={() => this.setState({ entityModal: { ...entityModal, show: false }, entityModalSubmitting: false })}
                    onSave={this.handleEntityModalSave}
                    submitting={this.state.entityModalSubmitting}
                    entityModal={entityModal}
                    businesses={businesses}
                    functions={functions}
                    onChange={(field, val) => this.setState(prev => ({
                        entityModal: {
                            ...prev.entityModal,
                            [field]: val,
                            errors: { ...prev.entityModal.errors, [field]: '' }
                        }
                    }))}
                />

                {/* 3. Quick Add Modal */}
                <QuickAddModal 
                    show={quickAddModal.show}
                    onClose={() => this.setState({ quickAddModal: { show: false }, quickAddSubmitting: false })}
                    onSubmit={this.handleQuickAddSubmit}
                    submitting={this.state.quickAddSubmitting}
                />
            </>
        );
    }
}

export default KpiCatalogView;
